// Utility: Schätzt, ob ein Template (Thema + Gruppen/Kompetenzen) ins PDF-Seitenlimit passt.
// Export: computeTemplatePdfFit({ templateJson, subjectTitle, topic }) -> { show, isOverLimit, text }
const PT_TO_PX = 96 / 72;

function measureTextWidthPx(text, fontPx, fontFamily = 'Helvetica, Arial, sans-serif') {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return 0;
  ctx.font = `${fontPx}px ${fontFamily}`;
  return ctx.measureText(String(text ?? '')).width || 0;
}

function wrapWordsToLines(text, maxWidthPx, fontPx, fontFamily) {
  const t = String(text ?? '').trim();
  if (!t) return [''];
  const words = t.split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';

  for (const w of words) {
    const candidate = current ? `${current} ${w}` : w;
    const wPx = measureTextWidthPx(candidate, fontPx, fontFamily);
    if (wPx <= maxWidthPx || !current) {
      current = candidate;
    } else {
      lines.push(current);
      current = w;
    }
  }
  if (current) lines.push(current);
  return lines.length ? lines : [''];
}

function estimateMetaBoxHeightPt({ subject, topic }) {
  // Angelehnt an PDF-Logik:
  // pad=8, subject font 20 (LineH 24), topic font 12 (LineH 16), max 3 topic lines
  const pad = 8;

  const pageWPt = 595.28; // A4 in pt (jsPDF default)
  const leftWPt = pageWPt - 80; // wie im PDF (pageW - 80)
  const innerWPt = leftWPt - pad * 2;

  const subjectFontPx = 20 * PT_TO_PX;
  const topicFontPx = 12 * PT_TO_PX;
  const fontFamily = 'Helvetica, Arial, sans-serif';

  const innerWPx = innerWPt * PT_TO_PX;

  const subjectLines = wrapWordsToLines(subject, innerWPx, subjectFontPx, fontFamily);

  const label = 'Inhalte und Themen:';
  const labelPx = measureTextWidthPx(label, topicFontPx, fontFamily);
  const gapPt = 6;
  const gapPx = gapPt * PT_TO_PX;

  const availFirstPx = Math.max(0, innerWPx - labelPx - gapPx);
  const availNextPx = innerWPx;

  // Topic: max 3 Zeilen
  const topicWords = String(topic ?? '').trim();

  // 1. Zeile ggf. enger (wegen Label)
  const firstLine = wrapWordsToLines(topicWords, availFirstPx, topicFontPx, fontFamily)[0] ?? '';
  const remaining = topicWords.replace(firstLine, '').trim();
  const restLines = remaining ? wrapWordsToLines(remaining, availNextPx, topicFontPx, fontFamily) : [];
  const topicLines = [firstLine, ...restLines].filter(Boolean).slice(0, 3);

  const subjectLineH = 24;
  const bodyLineH = 16;

  const boxHPt =
    pad +
    (subjectLines.length * subjectLineH) +
    10 +
    (Math.max(1, topicLines.length) * bodyLineH) +
    pad;

  return boxHPt;
}

function estimateCompetencyLinesCount(label, labelMaxWidthPt) {
  const fontPx = 10 * PT_TO_PX;
  const fontFamily = 'Helvetica, Arial, sans-serif';
  const maxWidthPx = Math.max(1, labelMaxWidthPt * PT_TO_PX);

  const lines = wrapWordsToLines(label, maxWidthPx, fontPx, fontFamily);
  return Math.max(1, lines.length);
}

function normalizeTemplateJsonLike(input) {
  // akzeptiert Objekt oder JSON-String; gibt Objekt mit sections und grouped_competencies zurück
  let tpl = input;
  if (typeof tpl === 'string') {
    try {
      tpl = JSON.parse(tpl);
    } catch {
      tpl = null;
    }
  }
  tpl = (tpl && typeof tpl === 'object' && !Array.isArray(tpl)) ? tpl : {};
  const sections = Array.isArray(tpl.sections) ? tpl.sections : [];

  const grouped =
    sections.find(s => s?.type === 'grouped_competencies' || s?.key === 'fachkompetenz') || { groups: [] };

  const groups = Array.isArray(grouped.groups) ? grouped.groups : [];

  const safeGroups = groups.map(g => ({
    title: String(g?.title ?? ''),
    items: Array.isArray(g?.items)
      ? g.items.map(it => ({ label: String(it?.label ?? '') }))
      : [],
  }));

  return {
    grouped: {
      groups: safeGroups,
    },
  };
}

export function computeTemplatePdfFit({ templateJson, subjectTitle = '', topic = '' }) {
  const subject = String(subjectTitle || '').trim();
  const t = String(topic || '').trim();

  const { grouped } = normalizeTemplateJsonLike(templateJson);

  // Konstanten wie in der PDF-Logik
  const pageBottomY = 780;
  const metaY = 30;
  const headerH = 34;

  const groupRowH = 26;
  const minItemRowH = 20;
  const labelLineHeight = 12;
  const labelPaddingTop = 4;
  const labelPaddingBottom = 4;

  // Spaltenbreiten (wie PDF): colNotDoneW=64, colArrowW=150, labelMaxWidth = colLabelW - 20
  const pageWPt = 595.28;
  const leftWPt = pageWPt - 80;
  const colNotDoneW = 64;
  const colArrowW = 150;
  const colLabelW = leftWPt - colNotDoneW - colArrowW;
  const labelMaxWidthPt = colLabelW - 20;

  const allowedPages = (subject.toLowerCase() === 'deutsch') ? 2 : 1;

  const groups = Array.isArray(grouped?.groups) ? grouped.groups : [];
  if (!groups.length) {
    return { show: false, isOverLimit: false, text: '' };
  }

  const metaHeight = estimateMetaBoxHeightPt({
    subject,
    topic: t,
  });

  const topY1 = metaY + metaHeight + 18;
  const available1 = Math.max(1, pageBottomY - topY1);

  // Folgeseiten starten ohne Meta-Box bei metaY
  const availableNext = Math.max(1, pageBottomY - metaY);
  const availableTotal = available1 + Math.max(0, allowedPages - 1) * availableNext;

  // Tabelleninhalt:
  let used = headerH;

  for (const g of groups) {
    const items = Array.isArray(g?.items) ? g.items : [];
    if (!items.length) continue;

    used += groupRowH;

    for (const it of items) {
      const label = String(it?.label ?? '');
      const linesCount = estimateCompetencyLinesCount(label, labelMaxWidthPt);
      const rowH = Math.max(
        minItemRowH,
        labelPaddingTop + linesCount * labelLineHeight + labelPaddingBottom
      );
      used += rowH;
    }
  }

  const fill = used / availableTotal;
  const fillPct = Math.round(fill * 100);

  // Dezent ab 90%
  const show = fill >= 0.9;
  const isOverLimit = fill > 1;

  if (!show) {
    return { show: false, isOverLimit: false, text: '' };
  }

  const limitText = allowedPages === 2 ? 'max. 2 Seiten' : 'max. 1 Seite';
  const text = isOverLimit
    ? `Hinweis: Das Kompetenzraster ist zu ${fillPct}% gefüllt und voraussichtlich zu lang (auf ${limitText}).`
    : `Hinweis: Das Kompetenzraster ist zu ${fillPct}% gefüllt – kleine Textänderungen könnten das Seitenlimit (${limitText}) überschreiten.`;

  return { show: true, isOverLimit, text };
}
